import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import DefaultLoader from "components/common/DefaultLoader";
import PrimaryExposure from "components/common/PrimaryExposure";
import { Fragment } from "react";
import { useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const CurrentExposures = ({
  orientation = "vertical",
  showPrimaryExposure = false,
}) => {
  const exposureListUpdating = useSelector(
    exposureSelectors.isExposureListUpdating
  );
  const inceptionYear = useSelector(programSelectors.getInceptionYear);
  const data = useSelector(exposureSelectors.getLatestExposures);
  const currentExposuresKeys = useSelector(
    (state) => state.print?.currentExposuresKeys
  );
  const { data: uiCustomisation } = staticDataService.useConfigQuery(
    "ui_customisation"
  );
  const numberFormatter = numbers.dpString(0);

  const filteredData =
    currentExposuresKeys == null
      ? data
      : data.filter((item) => currentExposuresKeys.includes(item.key));

  const exposureConfig = uiCustomisation?.currentExposures ?? {};
  const categoryMap = exposureConfig?.exposureCategoryMap ?? {};
  const categoryOrder = exposureConfig?.exposureCategoryOrder ?? [];
  const otherCategoryLabel =
    exposureConfig?.exposureCategoryOtherLabel || "Other";
  const hiddenUnits = exposureConfig?.hiddenUnits ?? [];
  const hasCategoryMap = Object.keys(categoryMap || {}).length > 0;

  const getLabelWithUnits = (label, units) => {
    if (
      !units ||
      hiddenUnits.some((u) => u.toLowerCase() === units.toLowerCase())
    )
      return label;
    return `${label} (${units})`;
  };

  const groupedData = hasCategoryMap
    ? filteredData.reduce((acc, item) => {
        const category =
          categoryMap?.[item.key] ??
          categoryMap?.[item.label] ??
          otherCategoryLabel;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(item);
        return acc;
      }, {})
    : {};

  const orderedCategoryNames = hasCategoryMap
    ? [
        ...categoryOrder.filter((name) => groupedData[name]?.length > 0),
        ...Object.keys(groupedData).filter(
          (name) =>
            !categoryOrder.includes(name) && groupedData[name]?.length > 0
        ),
      ]
    : [];

  const columnsPerRow = 8;
  const horizontalColumnWidth = `${(100 / columnsPerRow).toFixed(2)}%`;
  const isHorizontal = orientation === "horizontal";

  const buildHorizontalColumns = (items) =>
    Array.from(
      { length: Math.ceil(items.length / columnsPerRow) },
      (_, index) =>
        items.slice(index * columnsPerRow, (index + 1) * columnsPerRow)
    ).filter((column) => column.length > 0);

  const renderHorizontalTables = (items, keyPrefix, heading) => {
    const groupedColumns = buildHorizontalColumns(items);

    if (!groupedColumns.length) {
      return null;
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {heading && (
          <Typography variant={"subtitle2"} sx={{ fontWeight: 600 }}>
            {heading}
          </Typography>
        )}
        {groupedColumns.map((column, index) => {
          const paddedColumn =
            column.length < columnsPerRow
              ? [
                  ...column,
                  ...Array.from(
                    { length: columnsPerRow - column.length },
                    (_, fillerIndex) => ({
                      key: `__empty_${keyPrefix}_${index}_${fillerIndex}`,
                      label: "",
                      value: null,
                    })
                  ),
                ]
              : column;
          return (
            <TableContainer key={`current-exposures-row-${keyPrefix}-${index}`}>
              <Table
                className={"exposure-inputs fluid-width print-table-tiny"}
                size={"small"}
                sx={{ width: "100%", tableLayout: "fixed" }}
              >
                <colgroup>
                  <col style={{ width: "180px" }} />
                  {Array.from({ length: columnsPerRow }).map((_, colIndex) => (
                    <col
                      key={`current-exposures-col-${keyPrefix}-${index}-${colIndex}`}
                      style={{ width: horizontalColumnWidth }}
                    />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell className={"name-column"}>{"Measure"}</TableCell>
                    {paddedColumn.map((item) => (
                      <TableCell key={item.key} align={"right"}>
                        {item.label
                          ? getLabelWithUnits(item.label, item.units)
                          : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell className={"name-column"}>
                      {inceptionYear}
                    </TableCell>
                    {paddedColumn.map((item) => (
                      <TableCell
                        key={item.key}
                        className={"tabular"}
                        align={"right"}
                      >
                        {item.value == null ? "" : numberFormatter(item.value)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          );
        })}
      </Box>
    );
  };

  const buildVerticalColumns = (items) => {
    const columnCount = Math.max(1, Math.ceil(items.length / columnsPerRow));
    const rowsPerColumn = Math.ceil(items.length / columnCount);
    return Array.from({ length: columnCount }, (_, index) =>
      items.slice(index * rowsPerColumn, (index + 1) * rowsPerColumn)
    ).filter((column) => column.length > 0);
  };

  const renderVerticalTable = (columns, keyPrefix, heading) => {
    if (!columns.length) {
      return null;
    }

    const rowCount = Math.max(...columns.map((column) => column.length), 0);

    return (
      <Box>
        {heading && (
          <Typography variant={"subtitle2"} sx={{ fontWeight: 600, mb: 1 }}>
            {heading}
          </Typography>
        )}
        <TableContainer
          key={`current-exposures-${keyPrefix}`}
          sx={{ width: "auto", display: "inline-block" }}
        >
          <Table
            className={"exposure-inputs fluid-width print-table-tiny"}
            size={"small"}
            sx={{ width: "auto", tableLayout: "auto" }}
          >
            <colgroup>
              {columns.map((_, index) => (
                <Fragment key={`current-exposures-col-${keyPrefix}-${index}`}>
                  <col />
                  <col />
                  {index < columns.length - 1 && <col />}
                </Fragment>
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {columns.map((_, index) => (
                  <Fragment
                    key={`current-exposures-head-${keyPrefix}-${index}`}
                  >
                    <TableCell
                      className={"name-column"}
                      sx={{
                        whiteSpace: "nowrap",
                        width: "60%",
                        borderBottom: "none",
                      }}
                    >
                      {"Measure"}
                    </TableCell>
                    <TableCell
                      align={"right"}
                      sx={{
                        whiteSpace: "nowrap",
                        width: "40%",
                        borderBottom: "none",
                      }}
                    >
                      {inceptionYear}
                    </TableCell>
                    {index < columns.length - 1 && (
                      <TableCell sx={{ borderBottom: "none" }} />
                    )}
                  </Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: rowCount }).map((_, rowIndex) => (
                <TableRow
                  key={`current-exposures-row-${keyPrefix}-${rowIndex}`}
                >
                  {columns.map((column, columnIndex) => {
                    const item = column[rowIndex];
                    return (
                      <Fragment
                        key={`current-exposures-cell-${keyPrefix}-${columnIndex}-${rowIndex}`}
                      >
                        <TableCell
                          sx={{
                            paddingRight: "1rem !important",
                            width: "60%",
                            verticalAlign: "top",
                          }}
                        >
                          {item?.label
                            ? getLabelWithUnits(item.label, item.units)
                            : ""}
                        </TableCell>
                        <TableCell
                          className={"tabular"}
                          align={"right"}
                          sx={{ width: "40%" }}
                        >
                          {item?.value == null
                            ? ""
                            : numberFormatter(item.value)}
                        </TableCell>
                        {columnIndex < columns.length - 1 && <TableCell />}
                      </Fragment>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  const primaryExposureTable = showPrimaryExposure ? (
    <Box sx={{ width: "fit-content" }}>
      <div className={"print-scaled"}>
        <PrimaryExposure hideInput />
      </div>
    </Box>
  ) : null;

  if (exposureListUpdating) {
    return (
      <DefaultLoader
        style={{
          width: "200px",
          height: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        size={48}
      />
    );
  }

  if (isHorizontal) {
    if (hasCategoryMap) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {orderedCategoryNames.map((category) =>
            renderHorizontalTables(
              groupedData[category] || [],
              category,
              category
            )
          )}
          {primaryExposureTable}
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {renderHorizontalTables(filteredData, "default")}
        {primaryExposureTable}
      </Box>
    );
  }

  const verticalEntries = hasCategoryMap
    ? orderedCategoryNames.map((category) => ({
        key: category,
        heading: category,
        columns: buildVerticalColumns(groupedData[category] || []),
      }))
    : [
        {
          key: "default",
          heading: null,
          columns: buildVerticalColumns(filteredData),
        },
      ];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        alignItems: "flex-start",
      }}
    >
      {verticalEntries.map((entry) =>
        renderVerticalTable(entry.columns, entry.key, entry.heading)
      )}
      {primaryExposureTable}
    </Box>
  );
};

export default CurrentExposures;
